/**
 * V131 — Country-Aware Board Suggestions
 *
 * For each ISO-3166 alpha-2 country code, list the top-3 most-common board
 * IDs (as they appear inside `foreignCurriculum.js` / the master-data API
 * curriculum pack). When a parent's country is auto-detected from the phone
 * flag (or picked manually), the Board dropdown in
 * `ForeignCurriculumSelector` promotes these IDs to a "Popular in <Country>"
 * optgroup at the top so the correct pick is one tap away — the rest of the
 * boards remain accessible below in a "More boards" optgroup.
 *
 * Notes:
 *  - IDs must match the `boards[].id` values inside the active curriculum's
 *    board list; unknown IDs are silently skipped, so it is safe to ship a
 *    "wish list".
 *  - The order inside each array is the display order (index 0 = topmost).
 *  - This is a UI-only nudge — it does NOT filter or hide any board.
 */

export const POPULAR_BOARDS_BY_COUNTRY = {
    // United Kingdom — parents overwhelmingly ask for GCSE / A-Level / Edexcel
    GB: ["a-level", "gcse", "edexcel", "igcse", "ial"],

    // United States — AP first, then Common Core / Honors, and IB DP if in the IB pack
    US: ["ap", "common-core", "honors", "ib-dp", "sat", "act"],

    // United Arab Emirates — IGCSE, IB, MoE dominate expat + local schools
    AE: ["igcse-ae", "ib-ae", "moe", "british", "american", "cbse"],

    // Singapore — GCE A-Level (H2), O-Level, IB Diploma are the big three
    SG: ["a-level-sg", "o-level", "ib-sg", "igcse-sg", "sg-primary"],

    // Australia — HSC (NSW) is the largest, then VCE + QCE
    AU: ["hsc", "vce", "qce", "ib-au", "igcse-au", "wace", "sace"],

    // Canada — Ontario OSSD dominates enrolment, then Alberta + BC
    CA: ["ontario", "alberta", "bc", "ib-ca", "ap-ca", "quebec"],

    // India — CBSE first, then ICSE / ISC, then State boards
    IN: ["cbse-in", "icse-in", "isc-in", "state-in", "ib-in", "igcse-in"],

    // Gulf (SA/QA/OM/KW/BH) — British + CBSE + IB pack
    SA: ["british", "cbse", "ib", "american", "moe"],
    QA: ["british", "cbse", "ib", "american", "moe"],
    OM: ["british", "cbse", "ib", "american", "moe"],
    KW: ["british", "cbse", "ib", "american", "moe"],
    BH: ["british", "cbse", "ib", "american", "moe"],

    // Europe — National, IB, IGCSE/A-Level
    DE: ["national", "ib-eu", "igcse-eu", "european-school"],
    FR: ["national", "ib-eu", "igcse-eu", "european-school"],
    IT: ["national", "ib-eu", "igcse-eu", "european-school"],
    ES: ["national", "ib-eu", "igcse-eu", "european-school"],
    NL: ["national", "ib-eu", "igcse-eu", "european-school"],
    CH: ["national", "ib-eu", "igcse-eu", "european-school"],
    IE: ["national", "ib-eu", "igcse-eu", "european-school"],
    SE: ["national", "ib-eu", "igcse-eu", "european-school"],
    NO: ["national", "ib-eu", "igcse-eu", "european-school"],
    DK: ["national", "ib-eu", "igcse-eu", "european-school"],

    // New Zealand — NCEA, Cambridge, IB
    NZ: ["ncea", "cambridge-nz", "ib-nz"],

    // Hong Kong — HKDSE, British, IB
    HK: ["dse", "british-hk", "ib-hk", "ap-hk"],

    // East / SE Asia — British, IB, American
    JP: ["british", "ib", "american", "cbse", "national"],
    KR: ["british", "ib", "american", "cbse", "national"],
    CN: ["british", "ib", "american", "cbse", "national"],
    MY: ["british", "ib", "american", "cbse", "national"],
    TH: ["british", "ib", "american", "cbse", "national"],

    // Africa — Cambridge / IB / National
    ZA: ["cambridge", "ib", "national", "cbse"],
    KE: ["cambridge", "ib", "national", "cbse"],
    NG: ["cambridge", "ib", "national", "cbse"],
};

/**
 * Returns { popular: [...boardObjs], others: [...boardObjs] } for the given
 * country ISO. Preserves the popular ordering defined above and pushes all
 * remaining boards (in their original curriculum order) to `others`.
 * If the country has no mapping OR the active curriculum has ≤3 boards, we
 * return `popular: []` so the caller renders a single flat list unchanged.
 */
export function partitionBoards(countryIso, boardOptions) {
    const iso = String(countryIso || "").toUpperCase();
    const wishlist = POPULAR_BOARDS_BY_COUNTRY[iso] || [];
    if (!wishlist.length || !Array.isArray(boardOptions) || boardOptions.length <= 3) {
        return { popular: [], others: Array.isArray(boardOptions) ? boardOptions : [] };
    }
    const byId = new Map(boardOptions.map((b) => [b.id, b]));
    const popular = [];
    const seen = new Set();
    for (const id of wishlist) {
        if (byId.has(id) && !seen.has(id)) {
            popular.push(byId.get(id));
            seen.add(id);
        }
        if (popular.length === 3) break;
    }
    const others = boardOptions.filter((b) => !seen.has(b.id));
    return { popular, others };
}
